import React from "react";
import pl from "shared/messages/pl";
import { Flex } from "@fluentui/react-northstar";
import { useStore } from "stores/Stores";
import { observer } from "mobx-react-lite";
import styles from "./filters.module.scss";
import MultipleChoiceFilter from "shared/components/MultipleChoiceFilter/MultipleChoiceFilter";

export interface TaskBoardFiltersProps {
    disabled?: boolean;
    isMobile?: boolean;
}

const TaskBoardFilters: React.FC<TaskBoardFiltersProps> = observer((props) => {
    const { tasksStore } = useStore();

    const getMappedTaskTags = () => {
        const tags: string[] = [];
        tasksStore.tasks.forEach((task) => {
            const taskTags = task.tags.map((tag) => tag.description);
            taskTags.forEach((tagDescription) => {
                const isNewUniqueTag = tags.includes(tagDescription) === false;
                if (isNewUniqueTag) {
                    tags.push(tagDescription);
                }
            });
        });

        const mappedTags = tags.sort().map((tag) => ({ value: tag, valueAsString: tag }));

        return mappedTags;
    };

    const getSelectedMappedTaskTags = () =>
        tasksStore.selectedTaskTags.map((tag) => ({ value: tag, valueAsString: tag }));

    const getMappedAgenciesAndPeople = () => {
        const agencies = tasksStore.tasks
            .map((task) => task.intermediaryDisplayName!)
            .filter((v, i, s) => s.indexOf(v) === i && v !== undefined)
            .sort()
            .map((intermediary) => ({ value: { data: intermediary, isSeller: false }, valueAsString: intermediary }));

        const people = tasksStore.tasks
            .map((task) => task.sellerInformation!)
            .filter((v, i, s) => s.indexOf(v) === i && v !== undefined)
            .sort()
            .map((seller) => ({ value: { data: seller, isSeller: true }, valueAsString: seller }));

        const joinedAgenciesAndPeople = agencies.concat(people);

        return joinedAgenciesAndPeople;
    };

    const getSelectedMappedAgenciesAndPeople = () => {
        const agencies = tasksStore.selectedAgencies.map((agency) => ({
            value: agency,
            valueAsString: agency,
        }));

        const people = tasksStore.selectedPeople.map((seller) => ({
            value: seller,
            valueAsString: seller,
        }));

        const joinedAgenciesAndPeople = agencies.concat(people);

        return joinedAgenciesAndPeople;
    };

    const commonProps = {
        disabled: props.disabled,
    };

    return (
        <Flex wrap gap="gap.small" column={props.isMobile}>
            <MultipleChoiceFilter
                {...commonProps}
                placeholder={pl.taskBoardTopBar.filters.allTagsPlaceholder}
                availableChoices={getMappedTaskTags()}
                selectedChoices={getSelectedMappedTaskTags()}
                onSelectionChanged={(selection) => {
                    const copy = selection.slice();
                    tasksStore.setSelectedTaskTags(copy);
                }}
                className={styles.multipleChoiceFilter}
                clearable
            />
            <MultipleChoiceFilter
                {...commonProps}
                placeholder={pl.taskBoardTopBar.filters.allAgenciesAndSellersPlaceholder}
                availableChoices={getMappedAgenciesAndPeople()}
                selectedChoices={getSelectedMappedAgenciesAndPeople()}
                onSelectionChanged={(selection) => {
                    const agencies = selection
                        .filter((selected) => selected.isSeller === false)
                        .map((selected) => selected.data);
                    tasksStore.setSelectedAgencies(agencies);

                    const sellers = selection
                        .filter((selected) => selected.isSeller === true)
                        .map((selected) => selected.data);
                    tasksStore.setSelectedPeople(sellers);
                }}
                className={styles.agenciesAndSellersFilter}
                clearable
            />
        </Flex>
    );
});

export default TaskBoardFilters;
